import * as IoC from 'typescript-ioc';
import { action } from 'mobx';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import {
    CustomFieldsOptionsListDTO,
    DefaultPageConfigurationType,
    DefaultPageType,
    IAgendaModalProps,
    ICustomPagesDAO,
    IReplaysModalProps,
    ISessionsModalProps,
    PageLayoutType,
} from '../../../../../../../api/custom-pages.dao';
import { PassportPageSubWindowService } from '../passport-page.sub-window';
import { DefaultPageEntityConfig } from '../../../../../../../modules/passport-pages/default-pages/default-page-entity.config';
import { DefaultPageMapper } from '../../../../../../../modules/passport-pages/default-pages/default-page.mapper';
import { DefaultPageEntity } from '../../../../../../../modules/passport-pages/default-pages/default-page.entity';
import { IOrganizationsDAO } from '../../../../../../../api/organizations.dao';
import { DefaultPageViewModel } from './default-page.viewmodel';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import { MixPanelTrackingDefaultPagesMapper } from './mixpanel-tracking-default-page.mapper';
import { RegistrationConfigurationDAO } from '../../../../../../../api/implementations/registration-configuration.dao';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

const notesKeyMap = {
    [DefaultPageType.Agenda]: 'scheduleNotes',
    [DefaultPageType.Replays]: 'replaysNotes',
    [DefaultPageType.Session]: 'sessionNotes',
    [DefaultPageType.Support]: 'supportInformation'
};
export class DefaultPagesConfigurationVM {
    private initialDTO: DefaultPageConfigurationType;

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public get pageTypes(): DefaultPageType[] {
        return DefaultPageEntityConfig.getPageTypes();
    }

    @action
    public async openPageConfiguration(type: DefaultPageType): Promise<void> {
        if (this.defaultPageModal.hasSubWindowOpened(type)) {
            this.defaultPageModal.maximizeSubWindowByKey(type);
        } else {
            if (this.fetching[type]) return;
            this.fetching[type] = true;
            const vm = await this.getVmByType(type);
            this.openEditDefaultPage(vm);
        }
    }

    private formatMessage(description: MessageDescriptor): string {
        return this.intlFormatters.formatMessage(description);
    }

    private vmToEntity(vm: DefaultPageViewModel): DefaultPageEntity {
        const props = { ...vm.getEntityProps(), ...vm.getVmProps() };
        props.type = vm.getType();
        props.enabled = vm.isEnabled();
        props.publicEnabled = vm.isPublicEnabled();
        props.pageLinkEnabled = vm.isPageLinkEnabled();

        return DefaultPageMapper.toEntity(props);
    }

    private async getSessionsModalProps(): Promise<ISessionsModalProps> {
        const response = await Promise.all([
            this.getGeneralSessionsLayoutOptions(),
            this.fetchOrganizationTypes(),
            this.fetchCustomFieldsOptions()
        ]);
        if (response.some(resp => !resp)) return;
        const { types } = response[0];
        return {
            sessionsLayoutOptions: types,
            organizationTypes: response[1],
            customFieldsOptionsList: response[2]
        };
    }

    private async getReplaysModalProps(): Promise<IReplaysModalProps> {
        try {
            const response = await Promise.all([
                this.getUpcomingSessionsTypeOptions(),
                this.getReplaysLayoutOptions(),
                this.fetchOrganizationTypes(),
                this.fetchCustomFieldsOptions()
            ]);

            if (!response) return;

            return {
                upcomingSessionsTypeOptions: response[0].types,
                replaysLayoutOptions: response[1].types,
                organizationTypes: response[2],
                customFieldsOptionsList: response[3]
            };
        } catch (e) {
            DefaultPagesConfigurationVM.handleApiException(e);
        }
    }

    private async getAgendaModalProps(): Promise<IAgendaModalProps> {
        const response = await Promise.all([
            this.fetchOrganizationTypes(),
            this.fetchCustomFieldsOptions()
        ]);
        return {
            organizationTypes: response[0],
            customFieldsOptionsList: response[1]
        };
    }

    private fetchOrganizationTypes = async (query?: string): Promise<string[]> => {
        try {
            const params = {
                size: 100,
                page: 0,
                query: query ? query.trim() : '',
                ...this.eventParams,
            };
            const response = await this.organizationsDAO.getOrganizationTypes(params);
            return response.content;
        } catch (error) {
            DefaultPagesConfigurationVM.handleApiException(error);
        }
    };

    private fetchCustomFieldsOptions = async (): Promise<CustomFieldsOptionsListDTO[]> => {
        try {
            const response = await this.registrationConfigurationDAO.getCustomFieldsOptionsList(
                this.eventParams.eventId,
                this.eventParams.accountId,
            );
            return response.fields;
        } catch (error) {
            DefaultPagesConfigurationVM.handleApiException(error);
        }
    };

    @action
    private async openEditDefaultPage(vm: DefaultPageViewModel): Promise<void> {
        const type = vm.getType();
        const isSessionPage = type === DefaultPageType.Session;
        const isAgendaPage = type === DefaultPageType.Agenda;
        const isReplaysPage = type === DefaultPageType.Replays;
        const _props = isSessionPage
            ? await this.getSessionsModalProps()
            : isAgendaPage
            ? await this.getAgendaModalProps()
            : isReplaysPage
            ? await this.getReplaysModalProps()
            : {}
        ;
        this.fetching[type] = false;
        if (isSessionPage && !_props) return;

        this.setInitialDTO(this.vmToEntity(vm).toDTO());
        this.defaultPageModal.openEditDefaultPage({
            pageVm: vm,
            title: this.formatMessage({
                id: `oe.defaultPage.titleLabel.${type}`,
                defaultMessage: `Configure ${vm.pageName}`,
            }),
            handlers: {
                onApply: { handler: async () => this.savePageConfiguration(vm) },
                onApplyAndClose: { handler: async () => this.savePageConfiguration(vm), shouldClose: true },
            },
            ..._props,
        });
    }

    private async getVmByType(type: DefaultPageType): Promise<DefaultPageViewModel> {
        const dto = await this.getPageConfigurationByType(type);
        const notesKey = notesKeyMap[dto.type];
        if (notesKey) {
            dto[notesKey] = transpileInlineStylesToClasses(dto[notesKey]);
        }

        const entity = DefaultPageMapper.toEntity(dto);

        const vm = DefaultPageViewModel.create(entity);
        await vm.fetchSettings();
        return vm;
    }

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly intlFormatters: IntlShape,
        private readonly onSuccess: () => void,
        @IoC.Inject private readonly customPagesDAO: ICustomPagesDAO = IoC.Container.get(ICustomPagesDAO),
        @IoC.Inject private readonly organizationsDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly registrationConfigurationDAO: RegistrationConfigurationDAO = IoC.Container.get(RegistrationConfigurationDAO),
    ) { }

    private readonly defaultPageModal: PassportPageSubWindowService = PassportPageSubWindowService.init();
    private fetching: Partial<Record<DefaultPageType, boolean>> = {};

    private get eventParams(): { accountId: number; eventId: number } {
        return { accountId: this.accountId, eventId: this.eventId };
    }

    private async getGeneralSessionsLayoutOptions() {
        return await this.customPagesDAO.getSessionsPageLayoutOptions({...this.eventParams, layout: PageLayoutType.SESSION_LAYOUT });
    }

    private async getUpcomingSessionsTypeOptions() {
        return await this.customPagesDAO.getUpcomingSessionsTypeOptions(this.eventParams);
    }

    private async getReplaysLayoutOptions() {
        return await this.customPagesDAO.getReplaysPageLayoutOptions({...this.eventParams, layout: PageLayoutType.REPLAYS_LAYOUT });
    }

    @action.bound
    private async getPageConfigurationByType(type: DefaultPageType): Promise<DefaultPageConfigurationType> {
        try {
            return await this.customPagesDAO.getDefaultPageConfigByType(type, this.eventParams);
        } catch (e) {
            DefaultPagesConfigurationVM.handleApiException(e);
        }
    }
    @action.bound
    private async savePageConfiguration(pageVm: DefaultPageViewModel): Promise<void> {
        try {
            const dto = this.vmToEntity(pageVm).toDTO();
            const notesKey = notesKeyMap[dto.type];
            if (notesKey) {
                dto[notesKey] = transpileClassesToInlineStyles(dto[notesKey]);
            }
            await this.customPagesDAO.updateDefaultPageConfig(dto, this.eventParams);
            this.onSuccess();
            this.trackDefaultPageConfiguration(dto);
            this.setInitialDTO(dto);
        } catch (e) {
            DefaultPagesConfigurationVM.handleApiException(e);
        }
    }

    public hasSubWindowOpened(key: number | string): boolean {
        return this.defaultPageModal.hasSubWindowOpened(key);
    }

    private trackDefaultPageConfiguration(dto: DefaultPageConfigurationType): void {
        const trackingPages = [
            DefaultPageType.Agenda,
            DefaultPageType.Session,
            DefaultPageType.Showcase,
            DefaultPageType.Support,
            DefaultPageType.Welcome,
            DefaultPageType.Speakers,
            DefaultPageType.Replays
        ];
        if (trackingPages.includes(dto.type)) {
            const { eventData, eventName } = MixPanelTrackingDefaultPagesMapper.dataToTrackingEvent(dto, this.initialDTO);
            this.trackingService.get().track(eventName, {...eventData});
        }
    }

    private setInitialDTO(dto: DefaultPageConfigurationType): void {
        this.initialDTO = dto;
    }

    public closeAllSubWindows() {
        this.defaultPageModal.subWindows.forEach(el => el.close());
    }
}